/**
 * Lane 3 — effect on goals (spec #44 §1.2).
 *
 * The goal header comes from `GET /api/plan` and the cards from
 * `GET /api/plan/bets`, grouped by `commitment.owner_domain`. A tenant
 * with no approved plan answers `{status:"none"}` and gets the honest
 * empty state — never a placeholder metric, and never a zero drawn as if
 * it were a measurement.
 */

import { SkeletonBodyText, Text } from '@shopify/polaris';

import { betsByDomain } from '../../contexts/workspaceComposition';
import { useWorkspace } from '../../hooks/useWorkspace';
import { isApprovedPlan } from '../../types/plan';
import { BetCard } from './BetCard';
import { WorkspaceEmpty } from './WorkspaceEmpty';
import styles from './workspace.module.css';

export function EffectLane() {
  const { plan, bets, agents, loading, errors } = useWorkspace();
  const grouped = betsByDomain(bets);
  const names: Record<string, string> = {};
  agents.forEach((a) => {
    names[a.domain] = a.display_name;
  });

  return (
    <section className={styles.lane} aria-label="Effect on goals">
      <div className={styles.laneHead}>
        <span className={styles.laneTitle}>Effect on goals</span>
      </div>
      <div className={styles.laneCards}>
        {errors.plan ? (
          <WorkspaceEmpty kind="lane-error" reason={errors.plan} />
        ) : loading.plan && plan === null ? (
          <SkeletonBodyText lines={4} />
        ) : plan === null || !isApprovedPlan(plan) ? (
          <WorkspaceEmpty
            kind="no-plan"
            draftProposalId={
              plan && !isApprovedPlan(plan) ? plan.draft_proposal_id : null
            }
          />
        ) : (
          <>
            <div className={styles.tile}>
              <Text as="p" variant="bodySm" tone="subdued">
                Goal
              </Text>
              <Text as="p" variant="bodyMd" fontWeight="semibold">
                <span className={styles.mono}>{plan.goal.success_metric}</span>
              </Text>
              <div className={styles.subtle}>
                {plan.goal.target !== null && plan.goal.target !== undefined ? (
                  <>
                    target{' '}
                    <span className={styles.mono}>
                      {String(plan.goal.target)}
                    </span>
                  </>
                ) : (
                  'no target set'
                )}
                {plan.goal.horizon_end ? (
                  <>
                    {' · by '}
                    <span className={styles.mono}>
                      {plan.goal.horizon_end}
                    </span>
                  </>
                ) : null}
              </div>
            </div>
            {Object.keys(grouped).length === 0 ? (
              <Text as="p" tone="subdued">
                The plan is approved; no bets are attached to it yet.
              </Text>
            ) : (
              Object.entries(grouped).map(([domain, domainBets]) => (
                <div key={domain}>
                  <div className={styles.railHead}>
                    {names[domain] ?? domain}
                  </div>
                  {domainBets.map((bet) => (
                    <BetCard key={bet.id} bet={bet} />
                  ))}
                </div>
              ))
            )}
          </>
        )}
      </div>
    </section>
  );
}
